"use client";

import { useMemo, useState } from "react";
import { useRouter } from "@/i18n/navigation";
import { useTranslations } from "next-intl";

export interface SkillOption {
  name: string;
  category: string;
  categoryId: string;
}

export interface CategoryOption {
  id: string;
  label: string;
}

interface SearchFiltersProps {
  categories: CategoryOption[];
  skillOptions: SkillOption[];
  locations: string[];
  activeCategory?: string;
  activeLocation?: string;
  activeSkill?: string;
  activeQ?: string;
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 0.2s",
      }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function FilterSection({
  label,
  children,
  defaultOpen = true,
}: {
  label: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className="border-b"
      style={{ borderColor: "var(--color-border)" }}
    >
      <button
        type="button"
        className="w-full flex items-center justify-between py-3 text-sm font-semibold text-left"
        style={{ color: "var(--color-text)" }}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        <span style={{ color: "var(--color-muted)" }}>
          <ChevronIcon open={open} />
        </span>
      </button>
      {open && <div className="pb-4">{children}</div>}
    </div>
  );
}

export function SearchFilters({
  categories,
  skillOptions,
  locations,
  activeCategory,
  activeLocation,
  activeSkill,
  activeQ,
}: SearchFiltersProps) {
  const t = useTranslations("search");
  const router = useRouter();

  const [category, setCategory] = useState(activeCategory ?? "");
  const [location, setLocation] = useState(activeLocation ?? "");

  const [skill, setSkill] = useState(() => {
    const initialSkill = activeSkill ?? "";
    const initialCategory = activeCategory ?? "";
    if (!initialSkill || !initialCategory) return initialSkill;
    const isAllowed = skillOptions.some(
      (option) => option.name === initialSkill && option.categoryId === initialCategory
    );
    return isAllowed ? initialSkill : "";
  });

  const filteredSkillNames = useMemo(() => {
    const list = skillOptions
      .filter((option) => !category || option.categoryId === category)
      .map((option) => option.name)
      .filter(Boolean);
    return [...new Set(list)].sort((a, b) => a.localeCompare(b));
  }, [category, skillOptions]);

  function buildUrl(overrides: Record<string, string>) {
    const params = new URLSearchParams();
    if (activeQ) params.set("q", activeQ);
    const cat = overrides.category ?? category;
    const loc = overrides.location ?? location;
    const sk = overrides.skill ?? skill;
    if (cat) params.set("category", cat);
    if (loc) params.set("location", loc);
    if (sk) params.set("skill", sk);
    const qs = params.toString();
    return `/search${qs ? `?${qs}` : ""}`;
  }

  function apply(overrides: Record<string, string>) {
    router.push(buildUrl(overrides) as "/search");
  }

  function handleCategory(val: string) {
    setCategory(val);

    const nextSkill = !skill
      ? ""
      : skillOptions.some((option) => option.name === skill && (!val || option.categoryId === val))
        ? skill
        : "";

    if (nextSkill !== skill) setSkill(nextSkill);
    apply({ category: val, skill: nextSkill });
  }

  function handleLocation(val: string) {
    setLocation(val);
    apply({ location: val });
  }

  function handleSkill(val: string) {
    setSkill(val);
    apply({ skill: val });
  }

  const hasAnyFilter = !!(category || location || skill || activeQ);

  const selectClass =
    "w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 transition appearance-none cursor-pointer";

  const selectStyle = {
    background: "var(--color-bg)",
    borderColor: "var(--color-border)",
    color: "var(--color-text)",
  };

  return (
    <div
      className="rounded-2xl border p-4 sticky top-24"
      style={{
        background: "var(--color-surface)",
        borderColor: "var(--color-border)",
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <span
          className="text-sm font-bold uppercase tracking-widest"
          style={{ color: "var(--color-subtle)" }}
        >
          {t("filters")}
        </span>
        {hasAnyFilter && (
          <button
            type="button"
            className="text-xs hover:opacity-70 transition"
            style={{ color: "var(--color-brand)" }}
            onClick={() => {
              setCategory("");
              setLocation("");
              setSkill("");
              router.push(activeQ ? `/search?q=${encodeURIComponent(activeQ)}` as "/search" : "/search");
            }}
          >
            {t("clearButton")}
          </button>
        )}
      </div>

      {/* Category */}
      {categories.length > 0 && (
        <FilterSection label={t("filterCategory")}>
          <select
            className={selectClass}
            style={selectStyle}
            value={category}
            onChange={(e) => handleCategory(e.target.value)}
          >
            <option value="">{t("filterAll")}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </FilterSection>
      )}

      {/* Location */}
      {locations.length > 0 && (
        <FilterSection label={t("filterLocation")}>
          <select
            className={selectClass}
            style={selectStyle}
            value={location}
            onChange={(e) => handleLocation(e.target.value)}
          >
            <option value="">{t("filterAll")}</option>
            {locations.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </FilterSection>
      )}

      {/* Skill */}
      {skillOptions.length > 0 && (
        <FilterSection label={t("filterSkill")} defaultOpen={false}>
          <select
            className={selectClass}
            style={selectStyle}
            value={skill}
            onChange={(e) => handleSkill(e.target.value)}
          >
            <option value="">{t("filterAll")}</option>
            {filteredSkillNames.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </FilterSection>
      )}
    </div>
  );
}
